import {
  Box,
  Card,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Category as CategoryIcon,
  WarningAmber as WarningAmberIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

function MobilePrecautionsView({
  sortedCategoryEntries,
  onAddCategory,
  onAddPrecaution,
  onEditPrecaution,
  onDeletePrecaution,
  onDeleteCategory,
  setSelectedCategoryId,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box>
      {/* Header with Add Category Button */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {t("precautions.categoriesTitle")}
        </Typography>
        <IconButton
          color="success"
          onClick={onAddCategory}
          sx={{
            bgcolor: alpha(theme.palette.success.main, 0.1),
            "&:hover": {
              bgcolor: alpha(theme.palette.success.main, 0.2),
              transform: "scale(1.05)",
            },
            transition: "all 0.3s ease",
          }}
          aria-label={t("precautions.addCategoryButtonAria")}
        >
          <AddIcon />
        </IconButton>
      </Box>

      {/* Accordions for Categories */}
      {sortedCategoryEntries.length > 0 ? (
        <Box>
          {sortedCategoryEntries.map(([categoryId, group]) => (
            <Accordion
              key={categoryId}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: "0 0 16px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                  borderRadius: 2,
                  "&.Mui-expanded": {
                    borderRadius: "8px 8px 0 0",
                  },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ flex: 1, mr: 1 }}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      border: "2px solid",
                      borderColor: alpha(theme.palette.success.main, 0.2),
                      width: 40,
                      height: 40,
                    }}
                  >
                    <CategoryIcon
                      sx={{
                        color: "success.main",
                        fontSize: 20,
                      }}
                    />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight="600">
                      {group.category?.name || t("precautions.uncategorized")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {group.precautions.length} {t("precautions.items")}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={t("precautions.addPrecautionTooltip")}>
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCategoryId(categoryId);
                          onAddPrecaution();
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          cursor: "pointer",
                          color: "primary.main",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                            transform: "scale(1.05)",
                          },
                          transition: "all 0.3s ease",
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={t("precautions.addButtonAria")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                            setSelectedCategoryId(categoryId);
                            onAddPrecaution();
                          }
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </Box>
                    </Tooltip>
                    {group.precautions.length === 0 && (
                      <Tooltip title={t("precautions.deleteCategoryTooltip")}>
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteCategory(group.category);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            cursor: "pointer",
                            color: "error.main",
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            "&:hover": {
                              bgcolor: alpha(theme.palette.error.main, 0.2),
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.3s ease",
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={t(
                            "precautions.deleteCategoryButtonAria",
                            {
                              name: group.category?.name,
                            }
                          )}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.stopPropagation();
                              onDeleteCategory(group.category);
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </Box>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {/* Precautions List */}
                {group.precautions.length > 0 ? (
                  <List sx={{ p: 2 }}>
                    {group.precautions.map((precaution) => (
                      <Card
                        key={precaution.id}
                        elevation={1}
                        sx={{
                          mb: 1.5,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            boxShadow: theme.shadows[4],
                            borderColor: "info.main",
                            "& .precaution-actions": {
                              opacity: 1,
                            },
                          },
                        }}
                      >
                        <ListItem
                          sx={{
                            p: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                border: "2px solid",
                                borderColor: alpha(
                                  theme.palette.info.main,
                                  0.2
                                ),
                                width: 40,
                                height: 40,
                                mr: 1.5,
                              }}
                            >
                              <WarningAmberIcon
                                sx={{
                                  color: "info.main",
                                  fontSize: 20,
                                }}
                              />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                fontWeight="500"
                                color="text.primary"
                              >
                                {precaution.name}
                              </Typography>
                            }
                          />
                          <Box
                            className="precaution-actions"
                            sx={{
                              opacity: 0.7,
                              transition: "opacity 0.3s ease",
                              display: "flex",
                              gap: 0.5,
                            }}
                          >
                            <Tooltip title={t("precautions.editTooltip")}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onEditPrecaution(precaution)}
                                sx={{
                                  borderRadius: 2,
                                  "&:hover": {
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    transform: "scale(1.1)",
                                  },
                                  transition: "all 0.3s ease",
                                }}
                                aria-label={t("precautions.editButtonAria", {
                                  name: precaution.name,
                                })}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("precautions.deleteTooltip")}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => onDeletePrecaution(precaution)}
                                sx={{
                                  borderRadius: 2,
                                  "&:hover": {
                                    bgcolor: alpha(
                                      theme.palette.error.main,
                                      0.1
                                    ),
                                    transform: "scale(1.1)",
                                  },
                                  transition: "all 0.3s ease",
                                }}
                                aria-label={t("precautions.deleteButtonAria", {
                                  name: precaution.name,
                                })}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </ListItem>
                      </Card>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: "center", py: 4, px: 3 }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        p: 2,
                        borderRadius: "50%",
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        mb: 2,
                      }}
                    >
                      <WarningAmberIcon
                        sx={{
                          fontSize: 32,
                          color: "info.main",
                          opacity: 0.5,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight="500"
                    >
                      {t("precautions.noPrecautionsInCategory")}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {t("precautions.clickAddToCreatePrecaution")}
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Box
            sx={{
              display: "inline-flex",
              p: 3,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.success.main, 0.1),
              mb: 3,
            }}
          >
            <CategoryIcon
              sx={{
                fontSize: 48,
                color: "success.main",
                opacity: 0.7,
              }}
            />
          </Box>
          <Typography
            variant="h6"
            color="text.secondary"
            gutterBottom
            fontWeight="500"
          >
            {t("precautions.noCategories")}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default MobilePrecautionsView;
